import { isEmpty } from '@sgme/fp';
import classnames from 'classnames';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { CompactPercentFieldAdjuster } from 'components/shared/CompactPercentField/CompactPercentFieldAdjuster';
import { NewAmountInput } from 'components/shared/NewAmountInput/NewAmountInput';
import { PercentWithUnit } from 'components/shared/Percent/PercentWithUnit';
import type { HistoricalTrade } from 'models/forms/AddonManagementFormResult';
import type { AmountFieldMetadata } from 'models/forms/fields';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getTextAlignClassName } from 'shared/utils/getTextAlignClassName';
import type { ProductGraph } from 'state/product-description/model';
import { isUserPreferencesDarkMode } from 'state/selectors';
import styled from 'styled-components';

export interface AddonManagementHistoricFormProps {
  label: string;
  trade: HistoricalTrade | undefined;
  productDescription: ProductGraph;
  gridColumnSize: number;
  isUpfrontFeeColumnHidden: boolean;
  isOfferPriceColumnHidden: boolean;
  isFixedAmountInPercentColumnHidden: boolean;
  isTradingPriceColumnHidden: boolean;
  remunerationMode: Readonly<unknown>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  bgColor: string;
}

const StyledDiv = styled.div`
  color: grey;
  position: relative;
`;

type EmptyDivProps = { className?: string; bgColor: string; onMouseEnter?: () => void; onMouseLeave?: () => void };

const EmptyDiv: FC<EmptyDivProps> = ({ className, bgColor, onMouseEnter, onMouseLeave }) => {
  return (
    <div
      className={classnames(bgColor, className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="gridcell"
    ></div>
  );
};

export const AddonManagementPrimaryForm: FC<AddonManagementHistoricFormProps> = ({
  label,
  trade,
  productDescription,
  gridColumnSize,
  isUpfrontFeeColumnHidden,
  isOfferPriceColumnHidden,
  isFixedAmountInPercentColumnHidden,
  isTradingPriceColumnHidden,
  remunerationMode,
  onMouseEnter,
  onMouseLeave,
  bgColor,
}) => {
  return (
    <AddonManagementHistoricForm
      label={label}
      trade={trade}
      productDescription={productDescription}
      gridColumnSize={gridColumnSize}
      isUpfrontFeeColumnHidden={isUpfrontFeeColumnHidden}
      isOfferPriceColumnHidden={isOfferPriceColumnHidden}
      isFixedAmountInPercentColumnHidden={isFixedAmountInPercentColumnHidden}
      isTradingPriceColumnHidden={isTradingPriceColumnHidden}
      remunerationMode={remunerationMode}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      bgColor={bgColor}
    />
  );
};

const alignEndClass = getTextAlignClassName('Right');

export const AddonManagementHistoricForm: FC<AddonManagementHistoricFormProps> = ({
  label,
  trade,
  productDescription,
  isUpfrontFeeColumnHidden,
  isOfferPriceColumnHidden,
  isFixedAmountInPercentColumnHidden,
  isTradingPriceColumnHidden,
  remunerationMode,
  onMouseEnter,
  onMouseLeave,
  bgColor,
}) => {
  const notionalAmountMetadata = useFieldMetadata<AmountFieldMetadata>('NotionalAmount');
  const xMarginMetadata = useFieldMetadata<AmountFieldMetadata>('XMargin');
  const upfrontFeeMetadata = useFieldMetadata<AmountFieldMetadata>('UpfrontFee');
  const offerPriceMetadata = useFieldMetadata<AmountFieldMetadata>('OfferPrice');
  const fixedAmountInPercent = useFieldMetadata<AmountFieldMetadata>('FixedAmountInPercent');
  const isDarkMode = useSelector(isUserPreferencesDarkMode);

  const numberOfLeftEmptyCells = useMemo(() => {
    if (isEmpty(productDescription)) {
      return 0;
    }

    const addonRowMetadata = productDescription[0]?.children;

    if (isEmpty(addonRowMetadata)) {
      return 0;
    }

    const notionalAmountColumnIndex = addonRowMetadata
      .map(metadata => metadata.id)
      .findIndex(metadataId => metadataId === 'NotionalAmount');

    if (notionalAmountColumnIndex < 0) {
      return 0;
    }

    // Count 2 additional columns, not part of the Field metadata (checkbox & status columns)
    return notionalAmountColumnIndex + 2;
  }, [productDescription]);

  if (!trade) {
    return null;
  }
  const showUpfrontFeeValue = remunerationMode === 'Upfront';
  const showOfferPriceValue = remunerationMode === 'Reoffer';
  const showFixedAmountInPercentValue = remunerationMode === 'Premium';
  const precision = 4;
  const disabledColor = isDarkMode ? 'white !important' : 'black !important';

  return (
    <>
      {[...new Array(numberOfLeftEmptyCells)].map((_, index) => (
        <EmptyDiv key={index} bgColor={bgColor} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
      ))}
      <StyledDiv onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} role="gridcell" className={bgColor}>
        <div
          className="mx-1"
          style={{ minWidth: notionalAmountMetadata?.minWidth, maxWidth: notionalAmountMetadata?.maxWidth }}
        >
          <NewAmountInput
            value={trade.notional ?? null}
            currency={trade.currency ?? ''}
            e2e={`${label}-notional-value`}
            id={`${label}-notional-value`}
            isDisabled
            className="shadow-none"
          />
        </div>
      </StyledDiv>
      <EmptyDiv bgColor={bgColor} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
      <StyledDiv onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} role="gridcell" className={bgColor}>
        <div className="px-1">
          <CompactPercentFieldAdjuster
            id={`${label}-xMargin-value`}
            value={trade.xMargin !== null ? trade.xMargin : undefined}
            digits={precision}
            alignRight
            displayWithoutButtons
            style={{ minWidth: xMarginMetadata?.minWidth, maxWidth: xMarginMetadata?.maxWidth }}
          />
        </div>
      </StyledDiv>
      {!isTradingPriceColumnHidden && <EmptyDiv bgColor={bgColor} />}
      {!isUpfrontFeeColumnHidden && (
        <div className={classnames(bgColor, 'px-1')} role="gridcell">
          <PercentWithUnit
            value={showUpfrontFeeValue ? trade.upfrontFee : undefined}
            e2e={`${label}-upfrontFee-value`}
            className={classnames(alignEndClass, 'shadow-none text-muted')}
            isReadOnly
            decimalPadding
            precision={precision}
            unit={showUpfrontFeeValue && trade.upfrontFee ? '%' : ''}
            minWidth={`${upfrontFeeMetadata?.minWidth}px`}
            maxWidth={`${upfrontFeeMetadata?.maxWidth}px`}
            isDisabled
            disabledColor={disabledColor}
          />
        </div>
      )}
      {!isOfferPriceColumnHidden && (
        <div className={classnames(bgColor, 'px-1')} role="gridcell">
          <PercentWithUnit
            value={showOfferPriceValue ? trade.offerPrice : undefined}
            e2e={`${label}-offerPrice-value`}
            className={classnames(alignEndClass, 'shadow-none text-muted')}
            isReadOnly
            decimalPadding
            precision={precision}
            unit={showOfferPriceValue && trade.offerPrice ? '%' : ''}
            minWidth={`${offerPriceMetadata?.minWidth}px`}
            maxWidth={`${offerPriceMetadata?.maxWidth}px`}
            isDisabled
            disabledColor={disabledColor}
          />
        </div>
      )}
      {!isFixedAmountInPercentColumnHidden && (
        <div className={classnames(bgColor, 'px-2')} role="gridcell">
          <PercentWithUnit
            value={showFixedAmountInPercentValue ? trade.fixedAmount : undefined}
            e2e={`${label}-fixedAmountInPercent-value`}
            className={classnames(alignEndClass, 'shadow-none text-muted')}
            isReadOnly
            decimalPadding
            precision={precision}
            unit={showFixedAmountInPercentValue && trade.fixedAmount ? '%' : ''}
            minWidth={`${fixedAmountInPercent?.minWidth}px`}
            maxWidth={`${fixedAmountInPercent?.maxWidth}px`}
            isDisabled
            disabledColor={disabledColor}
          />
        </div>
      )}
      {<EmptyDiv bgColor={bgColor} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />}
    </>
  );
};
